import { IeLynxUser } from "../../common/models/eLynxUser";
import restService from "../rest-service/RestService";
import { UserAndRoleAdministrationUser } from "../user-role-administration-service/Models";
import { IUICustomizationPresetDTO, IUICustomizationSettings, IUICustomizationSettingsDTO } from "./Models";

type OrganizationParams = { organizationID: number };

const UICustomizationDataService = {

    getEffectiveUICustomizationSettings: async function (
        userID: number,
        signal?: AbortSignal
    ): Promise<IUICustomizationSettingsDTO | undefined> {

        const path = `users/${userID}/effectiveUICustomizationSettings`;

        const response = await restService.get<IUICustomizationSettings>(
            path,
            signal
        );

        const settings = response.data.settings
            ? JSON.parse(response.data.settings) as IUICustomizationSettingsDTO
            : undefined;

        return settings;
    },


    //////////////////////////////////////////////////////////////////////////////////////////////
    // #region UI Customization Settings Methods

    getUICustomizationSettings: async function (
        userID: number,
        signal?: AbortSignal
    ): Promise<IUICustomizationSettingsDTO | undefined> {

        const path = `users/${userID}/uiCustomizationSettings`;

        const response = await restService.get<IUICustomizationSettings>(
            path,
            signal
        );

        const settings = response.data.settings
            ? JSON.parse(response.data.settings) as IUICustomizationSettingsDTO
            : undefined;

        return settings;
    },

    saveUICustomizationSettings: async function (
        userID: number,
        settings: IUICustomizationSettingsDTO | undefined
    ): Promise<void> {
        const path = `users/${userID}/uiCustomizationSettings`;

        await restService.post<IUICustomizationSettings, void>(
            path,
            {
                settings: settings
                    ? JSON.stringify(settings)
                    : undefined
            }
        );
    },

    // #endregion UI Customization Settings Methods

    //////////////////////////////////////////////////////////////////////////////////////////////
    // #region UI Customization Preset Methods

    getUICustomizationPresets: async function (
        groupID: number,
        organizationID: number,
        signal?: AbortSignal
    ): Promise<Array<IUICustomizationPresetDTO>> {
        const path = `groups/${groupID}/customizationpresets`;

        const response = await restService.get<Array<IUICustomizationPresetDTO>, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    getUICustomizationPreset: async function (
        groupID: number,
        presetID: number,
        signal?: AbortSignal
    ): Promise<IUICustomizationPresetDTO> {
        const path = `groups/${groupID}/customizationpresets/${presetID}`;

        const response = await restService.get<IUICustomizationPresetDTO>(
            path,
            signal
        );

        return response.data;
    },

    getUICustomizationPresetUsers: async function (
        groupID: number,
        presetID: number,
        signal?: AbortSignal
    ): Promise<Array<UserAndRoleAdministrationUser>> {
        const path = `groups/${groupID}/customizationpresets/${presetID}/users`;

        const response = await restService.get<Array<UserAndRoleAdministrationUser>>(
            path,
            signal
        );

        return response.data;
    },

    saveUICustomizationPreset: async function (
        groupID: number,
        organizationID: number,        
        preset: IUICustomizationPresetDTO,
        signal?: AbortSignal
    ): Promise<IUICustomizationPresetDTO> {
        const path = `groups/${groupID}/customizationpreset`;

        const response = await restService.post<IUICustomizationPresetDTO, IUICustomizationPresetDTO, OrganizationParams>(
            path,
            preset,
            signal,
            undefined,
            { organizationID: organizationID }
        );

        return response.data;
    },

    deleteUICustomizationPreset: async function (
        groupID: number,
        presetID: number,
        signal?: AbortSignal
    ): Promise<void> {
        const path = `groups/${groupID}/customizationpresets/${presetID}`;

        await restService.delete<void>(
            path,
            signal
        );
    },

    checkPresetExists: async function (
        groupID: number,
        organizationID: number,
        presetName: string,
        signal?: AbortSignal
    ): Promise<boolean> {

        const path = `groups/${groupID}/customizationpresets/checkExists`;

        const response = await restService.get<boolean, OrganizationParams & { presetName: string; }>(
            path,
            signal,
            { organizationID: organizationID, presetName: presetName }
        );

        return response.data;
    },

    createDefaultUICustomizationPreset: function (): IUICustomizationPresetDTO {
        const defaultPreset: IUICustomizationPresetDTO = {
            id: -1,
            name: "",
            description: "",
            settingsJSON: ""
        };

        return defaultPreset;
    }

    // #endregion UI Customization Preset Methods
};

export default UICustomizationDataService;